import React, { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Building, MessageSquare, Store, User } from 'lucide-react';
import { StoreLoginForm } from '../../store/auth/StoreLogin';
import { OrganizerLoginForm } from '../../organizer/auth/OrganizerSendOtpPage';
import LoadingButton from '../../../components/form/LoadingButton';
import { apiPost } from '../../../utils/api';
import { showError, showSuccess } from '../../../utils/alert';
import AdvancedPhoneField from '../../../components/form/AdvancedPhoneField';
import { RECAPTCHA_SITE_KEY, isRecaptchaConfigured } from '../../../utils/captcha';
import { getIpAddress } from '../../../utils/getIpAddress';
import { getDeviceId } from '../../../api/token';
import LanguageSwitcher from '../../../components/layouts/LanguageSwitcher';

interface LoginFormData {
  phone: string;
}

type LoginTab = 'store' | 'owner' | 'organizer';
const ROLE_TABS_VISIBLE = false;

export function OwnerLoginForm() {
  const { t, i18n } = useTranslation();
  const { handleSubmit, register, formState: { errors } } = useForm<LoginFormData>();
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [userIp, setUserIp] = useState<string>('');
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = i18n.language === 'ar';

  const params = new URLSearchParams(location.search);
  const flowId = params.get('flowId');

  useEffect(() => {
    const fetchIp = async () => {
      const ip = await getIpAddress();
      setUserIp(ip);
    };
    fetchIp();
  }, []);

  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);

      if (!captchaToken) {
        showError(t('login.recaptchaRequired'));
        setLoading(false);
        return;
      }

      if (!userIp || userIp === 'unknown') {
        showError(t('login.ipDetectionFailed'));
        setLoading(false);
        return;
      }

      const deviceId = getDeviceId();
      if (!deviceId) {
        showError(t('login.deviceIdMissing'));
        setLoading(false);
        return;
      }

      const response = await apiPost('otp-code/send/owner', {
        phone: `${data.phone}`,
        deviceId: deviceId,
        ip: userIp,
        captchaToken: captchaToken
      }) as { status: string, code: string, phone: string };

      const message = t('signup_otp');
      showSuccess(message);

      recaptchaRef.current?.reset();
      setCaptchaToken(null);

      const redirect = params.get('redirect');
      const verifyUrl = flowId
        ? `/verify-otp?code=${response.code}&flowId=${flowId}`
        : `/verify-otp?code=${response.code}`;
      navigate(verifyUrl, { state: { phone: response.phone, redirect } });
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
      showError(err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <AdvancedPhoneField
        placeholder={t('login.phonePlaceholder')}
        register={register('phone', { required: t('login.phoneRequired') })}
        error={errors.phone}
      />

      {isRecaptchaConfigured() && (
        <div className="flex justify-center">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
          />
        </div>
      )}

      {!isRecaptchaConfigured() && (
        <div className="text-red-500 text-sm text-center">
          {t('login.recaptchaNotConfigured')}
        </div>
      )}

      {loading ? (
        <LoadingButton height={64} />
      ) : (
        <button
          type="submit"
          disabled={!captchaToken || !userIp || !isRecaptchaConfigured()}
          className={`bg-primaryButton text-white px-6 py-2 h-[64px] text-xl transition w-full rounded-[16px] capitalize font-semibold ${(!captchaToken || !userIp || !isRecaptchaConfigured()) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-85'}`}
        >
          {t('send_opt')}
        </button>
      )}
    </form>
  );
}

export default function OwnerSendOtp() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<LoginTab>('store');
  const isRTL = i18n.language === 'ar';

  const tabs = [
    { id: 'store', icon: Store, label: t('login.storeLogin'), subtitle: t('login.storeLoginSubtitle') },
    { id: 'owner', icon: User, label: t('login.ownerLogin'), subtitle: t('login.ownerLoginSubtitle') },
    { id: 'organizer', icon: Building, label: t('login.organizerLogin'), subtitle: t('login.organizerLoginSubtitle') }
  ] as const;

  const isStoreLogin = activeTab === 'store';
  const isOwnerLogin = activeTab === 'owner';

  const headerTitle = isOwnerLogin ? t('login.ownerLogin') : t('login.storeLoginHero');
  const headerSubtitle = isOwnerLogin
    ? t('login.ownerLoginSubtitle')
    : t('login.storeLoginHeroSubtitle');

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-light">
      <div className="flex min-h-screen flex-col md:flex-row bg-light md:overflow-hidden gap-5 lg:gap-6">
        <div className="hidden md:flex md:w-[48%] lg:w-[50%] h-auto p-3 lg:p-4 items-center">
          <img
            src="/main.png"
            alt={t('login.imageAlt')}
            className="w-[96%] h-auto max-h-[calc(100vh-8rem)] object-contain rounded-[28px] mx-auto"
          />
        </div>

        <div className="flex flex-1 items-center justify-center px-3 py-3 sm:px-4 md:px-6 lg:px-8 min-h-screen">
          <div className="w-full max-w-[32rem] bg-white rounded-[28px] shadow-xl border border-slate-200 p-3.5 sm:p-4 md:p-5">
            <div className="space-y-4 sm:space-y-5">
              <div className={`flex items-start justify-between gap-4 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                <div className={`space-y-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <h1 className="text-3xl sm:text-4xl font-black text-Black leading-tight max-w-[12ch]">
                    {headerTitle}
                  </h1>
                  <p className="text-sm sm:text-base text-slate-600 max-w-[34ch] leading-6">
                    {headerSubtitle}
                  </p>
                </div>
                <div className="shrink-0">
                  <LanguageSwitcher />
                </div>
              </div>

              {ROLE_TABS_VISIBLE ? (
                <div className="rounded-[24px] border border-slate-200 bg-slate-100 p-2">
                  <div role="tablist" className={`flex flex-col sm:flex-row gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {tabs.map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex-1 rounded-[20px] px-4 py-4 transition focus:outline-none focus:ring-4 focus:ring-primary/20 ${isRTL ? 'text-right' : 'text-left'} ${isActive ? 'bg-white text-primary shadow-sm border border-primary/20' : 'bg-transparent text-slate-600 hover:bg-white hover:text-slate-900'}`}
                        >
                          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                            <span className="text-slate-500">
                              <Icon size={20} />
                            </span>
                            <div>
                              <p className={`font-semibold ${isActive ? 'text-primary' : 'text-slate-800'}`}>
                                {tab.label}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-3.5 sm:p-4">
                <div className="space-y-4">
                  {isStoreLogin && <StoreLoginForm />}
                  {activeTab === 'owner' && (
                    <div className="space-y-3.5">
                      <button
                        type="button"
                        onClick={() => setActiveTab('store')}
                        className={`inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 ${isRTL ? 'flex-row-reverse self-end' : 'self-start'}`}
                      >
                        <ArrowRight size={16} className="rotate-180" />
                        {t('login.backToStoreLogin')}
                      </button>
                      <OwnerLoginForm />
                    </div>
                  )}
                  {activeTab === 'organizer' && <OrganizerLoginForm />}
                  {isStoreLogin && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('owner')}
                      className={`group w-full rounded-[16px] border border-slate-200 bg-white px-2.5 py-2 text-left transition duration-200 hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-lg ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      <div className={`flex items-center gap-2.5 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                        <div className="flex h-8 w-8 items-center justify-center rounded-2xl bg-primary/10 text-primary transition group-hover:bg-primary/15">
                          <User size={16} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`flex items-center justify-between gap-3 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                            <div>
                              <p className="text-[11px] font-semibold text-slate-900">{t('login.ownerCtaTitle')}</p>
                            </div>
                            <ArrowRight size={14} className={`shrink-0 text-slate-400 transition group-hover:translate-x-1 ${isRTL ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>

              <div className="rounded-[20px] border border-slate-200 bg-slate-100 p-3 text-center">
                <p className="text-[11px] font-semibold text-slate-700 mb-1">{t('login.needHelp')}</p>
                <a
                  href="https://wa.me/966555418013"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-[14px] bg-primary text-white px-3.5 py-2 text-[11px] font-semibold transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/20"
                >
                  <MessageSquare size={15} />
                  {t('login.contactSupport')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
